import { VIZ_CATEGORICAL } from '@splunk/visualization-color-palettes';
import {
    COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
    enhanceOptionsSchemaWithDynamicOptions,
    getPattern,
    LAYOUT_TYPE,
} from '../utils';

export const scatterOptionsSchema = enhanceOptionsSchemaWithDynamicOptions({
    x: {
        default: '> primary | seriesByIndex(0)',
        description: 'Specify the dataSource applied to the x-axis.',
        type: 'array',
        items: { type: ['string', 'number'] },
    },
    y: {
        default: '> primary | seriesByIndex(1)',
        description: 'Specify the dataSource applied to the y-axis.',
        type: 'array',
        items: { type: ['string', 'number'] },
    },
    category: {
        description: 'Specify a sequence of dataSource events to be plotted on the chart.',
        type: 'array',
        items: { type: ['string', 'number'] },
        default: '> primary | seriesByIndex(2)',
    },
    xField: {
        default: '> x | getField()',
        description: 'Specify the field that should be mapped to the x-axis.',
        type: 'string',
    },
    yField: {
        default: '> y | getField()',
        description: 'Specify the field that should be mapped to the y-axis.',
        type: 'string',
    },
    categoryField: {
        default: '> category | getField()',
        description: 'Specify the field that should be mapped to the series categories.',
        type: 'string',
    },
    backgroundColor: {
        default: '> themes.defaultBackgroundColor',
        description:
            'Specify the color used for the background. The default for enterprise light is "#ffffff". The default for enterprise dark is "#000000". The default for prisma dark is "#0b0c0e".',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        type: 'string',
    },
    legendDisplay: {
        description:
            'Specify the location of the legend on the panel. By default, legendDisplay is off when splitByLayout is trellis, and right otherwise. ',
        pattern: getPattern(['right', 'left', 'top', 'bottom', 'off']),
        type: 'string',
    },
    legendTruncation: {
        default: 'ellipsisEnd',
        description:
            'Specify how to display legend labels when they overflow the layout boundaries by replacing overflow text with an ellipsis.',
        pattern: getPattern(['ellipsisEnd', 'ellipsisMiddle', 'ellipsisStart', 'ellipsisOff']),
        type: 'string',
    },
    markerSize: {
        description: 'Specify, in pixels, the size of each scatter marker.',
        type: 'number',
        default: 4,
    },
    resultLimit: {
        default: 50000,
        description: 'Specify the number of data points rendered in a chart.',
        type: 'number',
    },
    seriesColors: {
        default: VIZ_CATEGORICAL,
        description: 'Specify the colors used for a series. For example, ["#FF0000", "#0000FF", "#008000"].',
        type: 'array',
        items: { type: 'string' },
    },
    seriesColorsByField: {
        description:
            'Specify the colors to use for specific categories, or series names, derived from distinct values in the selected category field. For example, {"user1": "#008000", "user2": "#FFA500"}.',
        type: 'object',
    },
    showXAxisExtendedRange: {
        default: true,
        description: 'Specify whether the x-axis should be extended to snap to whole major tick marks.',
        type: 'boolean',
    },
    showYAxisExtendedRange: {
        default: true,
        description: 'Specify whether the y-axis should be extended to snap to whole major tick marks.',
        type: 'boolean',
    },
    showXAxisWithZero: {
        default: false,
        description: 'Specify whether the x-axis range includes zero.',
        type: 'boolean',
    },
    showYAxisWithZero: {
        default: false,
        description: 'Specify whether the y-axis range includes zero.',
        type: 'boolean',
    },
    showRoundedXAxisLabels: {
        default: false,
        description: 'Specify whether to round x-axis values to the nearest integer.',
        type: 'boolean',
    },
    // showXMajorGridLines: {
    //     default: false,
    //     description: 'Specify whether major grid lines are visible on the x-axis.',
    //     type: 'boolean',
    // },
    showYMajorGridLines: {
        default: true,
        description: 'Specify whether major grid lines are visible on the y-axis.',
        type: 'boolean',
    },
    showXMinorGridLines: {
        default: false,
        description: 'Specify whether minor grid lines are visible on the x-axis.',
        type: 'boolean',
    },
    showYMinorGridLines: {
        default: false,
        description: 'Specify whether minor grid lines are visible on the y-axis.',
        type: 'boolean',
    },
    splitByLayout: {
        default: 'off',
        description:
            'Specify the layout method by which to display the visualization, which splits the data into individual visualizations based on a certain category.',
        type: 'string',
        pattern: getPattern([LAYOUT_TYPE.Off, LAYOUT_TYPE.Trellis]),
        enum: [LAYOUT_TYPE.Off, LAYOUT_TYPE.Trellis],
    },
    trellisBackgroundColor: {
        default: '> themes.defaultBackgroundColor',
        description:
            'Specify the color used for the trellis container background by using a hexadecimal code. For example, #0000FF.',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        type: 'string',
    },
    trellisColumns: {
        description:
            'Specify the number of visualizations to display in a given row of the trellis container. The remaining visualizations will wrap accordingly. If nothing is specified, it will be auto-set based on the trellisMinColumnWidth. The minimum value is 1.',
        type: 'number',
        minimum: 1,
        default: 3,
    },
    trellisMinColumnWidth: {
        default: 250,
        minimum: 1,
        description:
            'Specify the minimum width, in pixels, of each visualization in the trellis container. If the window or panel is resized, the remaining visualizations may be viewed by scrolling. The minimum value is 1.',
        type: 'number',
    },
    trellisPageCount: {
        default: 20,
        minimum: 1,
        description:
            'Specify the maximum number of visualizations to display in a single page in the trellis container. The remaining visualizations will paginate accordingly. The minimum value is 1.',
        type: 'number',
    },
    trellisRowHeight: {
        default: 180,
        minimum: 1,
        description:
            'Specify the height, in pixels, of each visualization in the trellis container. If the window or panel is resized, the remaining visualizations may be viewed by scrolling. The minimum value is 1.',
        type: 'number',
    },
    trellisSharedScale: {
        default: true,
        type: 'boolean',
        description: 'Specify whether all visualizations will share the same scale in the trellis container.',
    },
    trellisSortBy: {
        default: 'result',
        description:
            'Specify the sort method for the individual visualizations in the trellis container. Use result to sort by search result order, or name to sort alphabetically by visualization label.',
        type: 'string',
        pattern: getPattern(['result', 'name']),
        enum: ['result', 'name'],
    },
    trellisSortOrder: {
        default: 'ascending',
        description:
            'Specify the sort order for the individual visualizations in the trellis container. This setting applies to the sort method specified in trellisSortBy.',
        type: 'string',
        pattern: getPattern(['ascending', 'descending']),
        enum: ['ascending', 'descending'],
    },
    trellisSplitBy: {
        description:
            'Specify the field name of the column with categories used, or “aggregations”, to split the data into individual visualizations for trellis display, if applicable. If a SPL `timechart` command is used, this may default to the SPL `by` clause field. If a SPL `chart` command is used, this may default to the first string field.',
        type: 'string',
    },
    xAxisLabelRotation: {
        default: 0,
        description: 'Specify the rotation of the x-axis labels in degrees.',
        type: 'number',
        enum: [-90, -45, 0, 45, 90],
    },
    xAxisAbbreviation: {
        default: 'off',
        description: 'Specify whether to abbreviate large x-axis values with the closest SI prefix.',
        enum: ['off', 'auto'],
        pattern: getPattern(['auto', 'off']),
        type: 'string',
    },
    yAxisAbbreviation: {
        default: 'auto',
        description: 'Specify whether to abbreviate large y-axis values with the closest SI prefix.',
        enum: ['off', 'auto'],
        pattern: getPattern(['auto', 'off']),
        type: 'string',
    },
    xAxisLabelVisibility: {
        default: 'auto',
        description: 'Specify whether to show or hide labels on the x-axis.',
        pattern: getPattern(['auto', 'show', 'hide']),
        type: 'string',
    },
    yAxisLabelVisibility: {
        default: 'auto',
        description: 'Specify whether to show or hide labels on the y-axis.',
        pattern: getPattern(['auto', 'show', 'hide']),
        type: 'string',
    },
    xAxisLineVisibility: {
        default: 'hide',
        description: 'Specify whether to show or hide the x-axis line.',
        enum: ['hide', 'show'],
        pattern: getPattern(['show', 'hide']),
        type: 'string',
    },
    yAxisLineVisibility: {
        default: 'hide',
        description: 'Specify whether to show or hide the y-axis line.',
        enum: ['hide', 'show'],
        pattern: getPattern(['show', 'hide']),
        type: 'string',
    },
    xAxisMajorTickInterval: {
        default: 'auto',
        description:
            'Specify the spacing interval at which to place major tick marks along the numeric x-axis. By default, this value is automatically calculated based on the scale of the related axis.',
        type: ['string', 'number'],
    },
    yAxisMajorTickInterval: {
        default: 'auto',
        description:
            'Specify the spacing interval at which to place major tick marks along the numeric y-axis. By default, this value is automatically calculated based on the scale of the related axis.',
        type: ['string', 'number'],
    },
    xAxisMajorTickSize: {
        default: 6,
        description: 'Specify the size, in pixels, of major tick marks on the x-axis.',
        type: 'number',
    },
    yAxisMajorTickSize: {
        default: 6,
        description: 'Specify the size, in pixels, of major tick marks on the y-axis.',
        type: 'number',
    },
    xAxisMinorTickSize: {
        default: 6,
        description: 'Specify the size, in pixels, of minor tick marks on the x-axis.',
        type: 'number',
    },
    yAxisMinorTickSize: {
        default: 6,
        description: 'Specify the size, in pixels, of minor tick marks on the y-axis.',
        type: 'number',
    },
    xAxisMajorTickVisibility: {
        default: 'auto',
        description: 'Specify whether to show or hide major tick marks on the x-axis.',
        pattern: getPattern(['auto', 'show', 'hide']),
        type: 'string',
    },
    yAxisMajorTickVisibility: {
        default: 'auto',
        description: 'Specify whether to show or hide major tick marks on the y-axis.',
        pattern: getPattern(['auto', 'show', 'hide']),
        type: 'string',
    },
    xAxisMinorTickVisibility: {
        default: 'auto',
        description: 'Specify whether to show or hide minor tick marks on the x-axis .',
        pattern: getPattern(['auto', 'show', 'hide']),
        type: 'string',
    },
    yAxisMinorTickVisibility: {
        default: 'auto',
        description: 'Specify whether to show or hide minor tick marks on the y-axis.',
        pattern: getPattern(['auto', 'show', 'hide']),
        type: 'string',
    },
    xAxisMax: {
        default: 'auto',
        description: 'Specify the maximum number for the range of the visible x-axis.',
        type: ['string', 'number'],
    },
    yAxisMax: {
        default: 'auto',
        description: 'Specify the maximum number for the range of the visible y-axis.',
        type: ['string', 'number'],
    },
    xAxisMin: {
        default: 'auto',
        description: 'Specify the minimum number for the range of the visible x-axis.',
        type: ['string', 'number'],
    },
    yAxisMin: {
        default: 'auto',
        description: 'Specify the minimum number for the range of the visible y-axis.',
        type: ['string', 'number'],
    },
    xAxisScale: {
        default: 'linear',
        description: 'Specify the type of scale that applies to a numerical x-axis.',
        pattern: getPattern(['linear', 'log']),
        type: 'string',
    },
    yAxisScale: {
        default: 'linear',
        description: 'Specify the type of scale that applies to a numerical y-axis.',
        pattern: getPattern(['linear', 'log']),
        type: 'string',
    },
    xAxisTitleText: {
        description: 'Specify the title of the x-axis.',
        type: 'string',
    },
    yAxisTitleText: {
        description: 'Specify the title of the y-axis.',
        type: 'string',
    },
    xAxisTitleVisibility: {
        default: 'show',
        description: 'Specify whether to show or hide the title of the x-axis.',
        enum: ['hide', 'show'],
        pattern: getPattern(['show', 'hide']),
        type: 'string',
    },
    yAxisTitleVisibility: {
        default: 'show',
        description: 'Specify whether to show or hide the title of the y-axis.',
        enum: ['hide', 'show'],
        pattern: getPattern(['show', 'hide']),
        type: 'string',
    },
});
